import type { Severity } from './vitals';

export interface ScoreOption {
  score: number;
  label: string;
  description?: string;
}

export interface ScoreItem {
  id: string;
  label: string;
  hint?: string;
  options: ScoreOption[];
  /** Item may be marked untestable (e.g. intubated, amputation). Untestable counts as 0. */
  allowUntestable?: boolean;
}

export type ScoreAnswers = Record<string, number | 'UN' | undefined>;

export interface ScoreResult {
  total: number;
  complete: boolean;
  untestable: string[];
}

export function sumScore(items: ScoreItem[], answers: ScoreAnswers): ScoreResult {
  let total = 0;
  let complete = true;
  const untestable: string[] = [];
  for (const item of items) {
    const answer = answers[item.id];
    if (answer === undefined) complete = false;
    else if (answer === 'UN') untestable.push(item.id);
    else total += answer;
  }
  return { total, complete, untestable };
}

// ---------------------------------------------------------------------------------------------
// Glasgow Coma Scale
// ---------------------------------------------------------------------------------------------

export const GCS_ADULT: ScoreItem[] = [
  {
    id: 'eye',
    label: 'Eye opening',
    options: [
      { score: 4, label: 'Spontaneous' },
      { score: 3, label: 'To sound / voice' },
      { score: 2, label: 'To pressure / pain' },
      { score: 1, label: 'None' },
    ],
  },
  {
    id: 'verbal',
    label: 'Verbal response',
    allowUntestable: true,
    options: [
      { score: 5, label: 'Oriented' },
      { score: 4, label: 'Confused' },
      { score: 3, label: 'Inappropriate words' },
      { score: 2, label: 'Incomprehensible sounds' },
      { score: 1, label: 'None' },
    ],
  },
  {
    id: 'motor',
    label: 'Motor response',
    options: [
      { score: 6, label: 'Obeys commands' },
      { score: 5, label: 'Localizes to pain' },
      { score: 4, label: 'Withdraws from pain (normal flexion)' },
      { score: 3, label: 'Abnormal flexion (decorticate)' },
      { score: 2, label: 'Extension (decerebrate)' },
      { score: 1, label: 'None' },
    ],
  },
];

/** Pediatric (preverbal, < 2 years) GCS modifications. */
export const GCS_PEDIATRIC: ScoreItem[] = [
  {
    id: 'eye',
    label: 'Eye opening',
    options: [
      { score: 4, label: 'Spontaneous' },
      { score: 3, label: 'To speech / sound' },
      { score: 2, label: 'To pain' },
      { score: 1, label: 'None' },
    ],
  },
  {
    id: 'verbal',
    label: 'Verbal response',
    allowUntestable: true,
    options: [
      { score: 5, label: 'Coos, babbles' },
      { score: 4, label: 'Irritable cry, consolable' },
      { score: 3, label: 'Cries to pain' },
      { score: 2, label: 'Moans to pain' },
      { score: 1, label: 'None' },
    ],
  },
  {
    id: 'motor',
    label: 'Motor response',
    options: [
      { score: 6, label: 'Normal spontaneous movement' },
      { score: 5, label: 'Withdraws to touch' },
      { score: 4, label: 'Withdraws to pain' },
      { score: 3, label: 'Abnormal flexion (decorticate)' },
      { score: 2, label: 'Extension (decerebrate)' },
      { score: 1, label: 'None' },
    ],
  },
];

export function interpretGcs(total: number): { severity: Severity; label: string; detail: string } {
  if (total <= 8) {
    return { severity: 'critical', label: 'Severe (≤ 8)', detail: 'Severe brain injury range. Anticipate need for airway protection.' };
  }
  if (total <= 12) {
    return { severity: 'caution', label: 'Moderate (9–12)', detail: 'Moderate brain injury range. Frequent neuro reassessment.' };
  }
  return { severity: 'normal', label: 'Mild (13–15)', detail: 'Mild brain injury range.' };
}

// ---------------------------------------------------------------------------------------------
// APGAR
// ---------------------------------------------------------------------------------------------

export const APGAR: ScoreItem[] = [
  {
    id: 'appearance',
    label: 'Appearance (color)',
    options: [
      { score: 2, label: 'Pink body and extremities' },
      { score: 1, label: 'Pink body, blue extremities' },
      { score: 0, label: 'Blue or pale all over' },
    ],
  },
  {
    id: 'pulse',
    label: 'Pulse',
    options: [
      { score: 2, label: '≥ 100 bpm' },
      { score: 1, label: '< 100 bpm' },
      { score: 0, label: 'Absent' },
    ],
  },
  {
    id: 'grimace',
    label: 'Grimace (reflex irritability)',
    options: [
      { score: 2, label: 'Cries, coughs, sneezes or pulls away' },
      { score: 1, label: 'Grimace' },
      { score: 0, label: 'No response' },
    ],
  },
  {
    id: 'activity',
    label: 'Activity (muscle tone)',
    options: [
      { score: 2, label: 'Active motion' },
      { score: 1, label: 'Some flexion' },
      { score: 0, label: 'Limp' },
    ],
  },
  {
    id: 'respiration',
    label: 'Respiration',
    options: [
      { score: 2, label: 'Strong cry' },
      { score: 1, label: 'Slow, irregular or weak cry' },
      { score: 0, label: 'Absent' },
    ],
  },
];

export function interpretApgar(total: number): { severity: Severity; label: string } {
  if (total >= 7) return { severity: 'normal', label: 'Reassuring (7–10)' };
  if (total >= 4) return { severity: 'caution', label: 'Moderately abnormal (4–6)' };
  return { severity: 'critical', label: 'Low (0–3)' };
}

// ---------------------------------------------------------------------------------------------
// qSOFA
// ---------------------------------------------------------------------------------------------

export interface QsofaInput {
  respiratoryRate: number;
  systolic: number;
  alteredMentation: boolean;
}

export function qsofa({ respiratoryRate, systolic, alteredMentation }: QsofaInput) {
  const criteria = {
    respiratoryRate: respiratoryRate >= 22,
    systolic: systolic <= 100,
    alteredMentation,
  };
  const score = Object.values(criteria).filter(Boolean).length;
  return {
    score,
    criteria,
    severity: (score >= 2 ? 'critical' : score === 1 ? 'caution' : 'normal') as Severity,
    label: score >= 2 ? 'Positive (≥ 2): high risk of poor outcome with suspected infection' : 'Negative (< 2)',
  };
}
